<script lang="ts">
  /**
   * Full-screen slide-in drawer from the right. Notion/ChatGPT style:
   * minimal chrome, large tap targets, no decoration.
   */
  import { page } from '$app/stores';
  import { onMount } from 'svelte';
  import LogoIcon from './LogoIcon.svelte';
  import CloseIcon from '$components/ui/icons/CloseIcon.svelte';
  import SunIcon from '$components/ui/icons/SunIcon.svelte';
  import MoonIcon from '$components/ui/icons/MoonIcon.svelte';
  import type { HeaderUser } from './Header.svelte';
  import { displayFor } from './Header.svelte';

  interface Props {
    open?: boolean;
    user?: HeaderUser | null;
  }
  let { open = $bindable(false), user = null }: Props = $props();

  const NAV = [
    { href: '/search', label: 'Іздеу' },
    { href: '/learn',  label: 'Оқу' },
    { href: '/test',   label: 'Тест' },
    { href: '/about',  label: 'Біз туралы' }
  ];

  let theme = $state<'light' | 'dark'>('light');
  onMount(() => {
    theme = (localStorage.getItem('theme') as 'light' | 'dark') ?? 'light';
  });

  function toggleTheme(): void {
    theme = theme === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }

  function close(): void { open = false; }

  // Lock body scroll while drawer open.
  $effect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    if (open) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  });

  // Close on Escape.
  function handleKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && open) close();
  }

  // Close on route change.
  $effect(() => {
    $page.url.pathname;
    open = false;
  });

  const pathname = $derived($page.url.pathname);
  function isActive(href: string): boolean {
    if (href === '/search') return pathname === '/' || pathname.startsWith('/search');
    return pathname.startsWith(href);
  }
</script>

<svelte:window onkeydown={handleKey} />

<div
  class="overlay"
  class:open
  onclick={close}
  role="presentation"
  aria-hidden="true"
></div>

<aside
  class="drawer"
  class:open
  aria-hidden={!open}
  aria-label="Мобильді меню"
>
  <div class="drawer-header">
    <a href="/" class="logo" onclick={close} aria-label="Zetix — басты бет">
      <LogoIcon size={20} />
      <span>Zetix</span>
    </a>
    <button class="close-btn" onclick={close} aria-label="Менюды жабу" type="button">
      <CloseIcon size={20} />
    </button>
  </div>

  <nav class="drawer-nav" aria-label="Мобильді навигация">
    {#each NAV as item (item.href)}
      <a
        href={item.href}
        class="drawer-link"
        class:active={isActive(item.href)}
        aria-current={isActive(item.href) ? 'page' : undefined}
        onclick={close}
      >
        {item.label}
      </a>
    {/each}
  </nav>

  <div class="drawer-footer">
    <button class="theme-row" onclick={toggleTheme} type="button">
      {#if theme === 'dark'}
        <SunIcon size={18} />
        <span>Күндізгі режим</span>
      {:else}
        <MoonIcon size={18} />
        <span>Түнгі режим</span>
      {/if}
    </button>

    {#if user}
      <a href={user.href ?? '/profile'} class="profile-row" onclick={close}>
        <span class="profile-avatar">
          {(displayFor(user) || '?').slice(0, 1).toUpperCase()}
        </span>
        <span class="profile-name">{displayFor(user)}</span>
      </a>
    {:else}
      <a href="/auth/login" class="signin-row" onclick={close}>Кіру</a>
    {/if}
  </div>
</aside>

<style>
  .overlay {
    position: fixed;
    inset: 0;
    z-index: var(--z-overlay);
    background: rgba(0, 0, 0, 0.4);
    opacity: 0;
    pointer-events: none;
    transition: opacity var(--dur-slow) var(--ease);
  }
  .overlay.open { opacity: 1; pointer-events: auto; }
  @media (min-width: 768px) { .overlay { display: none; } }

  .drawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: var(--z-overlay);
    width: min(320px, 80vw);
    background: var(--color-bg);
    border-left: 1px solid var(--color-border-subtle);
    transform: translateX(100%);
    transition: transform var(--dur-slow) var(--ease-spring);
    display: flex;
    flex-direction: column;
  }
  .drawer.open { transform: translateX(0); }
  @media (min-width: 768px) { .drawer { display: none; } }

  .drawer-header {
    height: var(--header-h);
    padding: 0 var(--space-4);
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .logo {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--color-fg);
    font-weight: var(--fw-semibold);
    font-size: var(--text-base);
  }
  .close-btn {
    display: inline-grid;
    place-items: center;
    height: 32px;
    width: 32px;
    border: 0;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--color-fg);
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease);
  }
  .close-btn:hover { background: var(--color-bg-muted); }

  .drawer-nav {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: var(--space-4) var(--space-2);
    gap: var(--space-1);
    overflow-y: auto;
  }
  .drawer-link {
    padding: var(--space-4);
    font-size: var(--text-lg);
    font-weight: var(--fw-medium);
    color: var(--color-fg-secondary);
    border-radius: var(--radius-md);
    transition:
      background-color var(--dur-fast) var(--ease),
      color var(--dur-fast) var(--ease);
  }
  .drawer-link:hover { color: var(--color-fg); background: var(--color-bg-muted); }
  .drawer-link.active { color: var(--color-fg); background: var(--color-bg-muted); }

  .drawer-footer {
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    border-top: 1px solid var(--color-border-subtle);
  }
  .theme-row {
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border: 0;
    border-radius: var(--radius-md);
    background: var(--color-bg-muted);
    color: var(--color-fg);
    font-size: var(--text-sm);
    font-weight: var(--fw-medium);
    cursor: pointer;
    width: 100%;
    transition: background-color var(--dur-fast) var(--ease);
  }
  .theme-row:hover { background: var(--color-border-subtle); }

  .profile-row {
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    background: var(--color-bg-muted);
    color: var(--color-fg);
    font-size: var(--text-sm);
    font-weight: var(--fw-medium);
  }
  .profile-avatar {
    display: inline-grid;
    place-items: center;
    height: 28px;
    width: 28px;
    border-radius: var(--radius-full);
    background: var(--color-fg);
    color: var(--color-bg);
    font-size: var(--text-xs);
    font-weight: var(--fw-semibold);
  }
  .profile-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; }

  .signin-row {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 var(--space-4);
    border-radius: var(--radius-md);
    background: var(--color-fg);
    color: var(--color-bg);
    font-size: var(--text-sm);
    font-weight: var(--fw-medium);
  }

  @media (prefers-reduced-motion: reduce) {
    .overlay, .drawer { transition: none !important; }
  }
</style>
